import type { Layer } from '@pn/core/domain/layer';
import type { UserSettings } from '@pn/core/domain/settings';
import { defaultUserSettings } from '@pn/core/domain/settings';
import {
  updateUserProfile,
  type User,
  type UserForm,
} from '@pn/core/domain/user';
import { assert } from '@pn/core/utils/assert';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { CURRENT_USER_SLICE_NAME } from './currentUserAccessors';

type State = {
  isError: boolean;
  user: User | undefined;
  settings: UserSettings;
  isAdminMode: boolean;
};

const initialState: State = {
  isError: false,
  user: undefined,
  settings: defaultUserSettings,
  isAdminMode: false,
};

const createUserSlice = (sliceName: string) =>
  createSlice({
    name: sliceName,
    initialState,
    reducers: {
      request: (state) => {
        state.isError = false;
      },
      receive: (
        state,
        {
          payload: { user, settings },
        }: PayloadAction<{ user: User; settings: UserSettings }>
      ) => {
        state.user = user;
        state.settings = settings;
      },
      error: (state) => {
        state.isError = true;
      },
      updateProfile: (
        state,
        { payload: userForm }: PayloadAction<UserForm>
      ) => {
        assert(state.user, 'Unable to update undefined user');
        state.user = updateUserProfile(state.user, userForm);
      },
      updateStackCompanyId: (state, { payload: id }: PayloadAction<string>) => {
        assert(state.user, 'Unable to update undefined user');
        state.user.stackCompanyId = id;
      },
      toggleAdminMode: (state) => {
        state.isAdminMode = !state.isAdminMode;
      },
      updateChartConfig: (
        state,
        { payload: config }: PayloadAction<Partial<UserSettings['chartConfig']>>
      ) => {
        state.settings.chartConfig = {
          ...state.settings.chartConfig,
          ...config,
        };
      },
      updateGrids: (
        state,
        { payload: grids }: PayloadAction<Partial<UserSettings['grids']>>
      ) => {
        state.settings.grids = {
          ...state.settings.grids,
          ...grids,
        };
      },
      updateBaseLayers: (
        state,
        {
          payload: baseLayers,
        }: PayloadAction<Partial<UserSettings['baseLayers']>>
      ) => {
        state.settings.baseLayers = {
          ...state.settings.baseLayers,
          ...baseLayers,
        };
      },
      updatePnLayers: (
        state,
        {
          payload: { itemId, layers },
        }: PayloadAction<{ itemId: string; layers: Layer[] }>
      ) => {
        state.settings.pnLayerStyles[itemId] = layers;
      },
      removePnLayers: (state, { payload: itemId }: PayloadAction<string>) => {
        delete state.settings.pnLayerStyles[itemId];
      },
    },
  });

export const currentUserSlice = createUserSlice(CURRENT_USER_SLICE_NAME);
